import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

/**
 * Settings scaffolding shared across the Setup screens (doc 01 A6, doc 13).
 * Structural only — no domain copy lives here.
 */
export function SettingsHeader({ title, blurb }: { title: string; blurb?: string }) {
  return (
    <div style={{ marginBottom: 'var(--space-16)' }}>
      <h1 className="rn-card-title">{title}</h1>
      {blurb ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {blurb}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Honest "not yet available" panel for a NAMED backend gap (doc 13 B-items /
 * §2.3). Distinct from `NotYetObserved` (which is the *history*-gap copy): this
 * one states plainly that the surface exists in the design but its route hasn’t
 * shipped — no controls, no fabricated rows.
 */
export function NotYetPanel({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="rn-card" role="status">
      <h2 className="rn-card-title">{title}</h2>
      <p className="rn-muted">{detail}</p>
    </div>
  );
}

export function SettingsCard({ children }: { children: ReactNode }) {
  return <section className="rn-card">{children}</section>;
}

/**
 * The save/cancel bar every Settings screen ends with.
 *
 * It states the form's status in words rather than relying on a button's
 * enabled-ness to imply it: "Unsaved changes", "Saved", or the reason saving is
 * blocked. `aria-live` announces the outcome, so a screen-reader user learns a
 * save succeeded without having to go looking for the change.
 */
export function SettingsActions({
  dirty,
  valid,
  saving,
  saved,
  error,
  onSave,
  onCancel,
  saveLabel = 'Save changes',
}: {
  dirty: boolean;
  valid: boolean;
  saving: boolean;
  saved: boolean;
  error?: string | null;
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
}) {
  const status = error
    ? { tone: 'error', text: error }
    : saving
      ? { tone: 'dirty', text: 'Saving…' }
      : !valid
        ? { tone: 'error', text: 'Fix the highlighted fields before saving.' }
        : dirty
          ? { tone: 'dirty', text: 'Unsaved changes' }
          : saved
            ? { tone: 'saved', text: 'Saved' }
            : null;

  return (
    <div className="set-actions">
      <button
        type="button"
        className="signin-devbtn"
        style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
        disabled={saving || !dirty || !valid}
        onClick={onSave}
      >
        {saving ? 'Saving…' : saveLabel}
      </button>
      <button type="button" className="rn-btn-quiet" disabled={saving || !dirty} onClick={onCancel}>
        Cancel
      </button>
      <span
        className="set-actions-note"
        data-tone={status?.tone}
        style={status?.tone === 'error' ? { color: 'var(--color-urgent-deep)' } : undefined}
        role="status"
        aria-live="polite"
      >
        {status?.text ?? ''}
      </span>
    </div>
  );
}

/**
 * A labelled field that can carry a validation message.
 *
 * This component is where every Settings form's validation lands — 78 controls
 * across 12 screens go through it — and it had two problems, both of which only
 * show up if you are not looking at the screen.
 *
 * 1. The message was red text sitting after the control, related to it by
 *    nothing but position. Nine of the 171 form controls in the admin carried
 *    `aria-invalid`, three carried `aria-describedby`, and all of those were
 *    hand-written on individual screens. A screen-reader user tabbing into a
 *    field that had failed validation was told the field's name and nothing
 *    else, and the reason it was rejected was a paragraph they had to go find.
 * 2. The message was INSIDE the `<label>`. An implicit label contributes its
 *    whole text content to the accessible NAME of the control it wraps, so the
 *    error was not merely unassociated — it was being spliced into the field's
 *    name ("Display name An organization needs a name."), which is also how the
 *    optional `hint` behaved.
 *
 * Both are fixed here rather than on 12 screens: the note moves out of the
 * label so it stops polluting the name, gets a stable id, and is attached to
 * the control as a DESCRIPTION, with `aria-invalid` set when it is an error.
 * Callers pass a native `input`/`select`/`textarea` and need to know none of it.
 *
 * `aria-describedby` and not `role="alert"` deliberately: these validators run
 * on every keystroke, so an alert would interrupt the reader on each character.
 * A description is announced when the field is entered, which is when it is
 * useful, and `SettingsActions` already announces "Fix the highlighted fields"
 * politely at the point of save.
 */
const ASSOCIABLE = new Set(['input', 'select', 'textarea']);

export function SettingsField({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  const noteId = `${useId()}note`;
  const note = error ?? hint;

  // Only native form controls are cloned. A caller passing something else (or
  // several things) keeps today's rendering rather than silently receiving an
  // `aria-describedby` that points at a description of a different control.
  const control =
    note && isValidElement(children) && ASSOCIABLE.has(children.type as string)
      ? cloneElement(children as ReactElement<Record<string, unknown>>, {
          'aria-describedby': noteId,
          'aria-invalid': error ? true : undefined,
        })
      : children;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)', minWidth: 0 }}>
      <label style={{ display: 'grid', gap: 'var(--space-6)', minWidth: 0 }}>
        <span className="rn-muted">{label}</span>
        {control}
      </label>
      {error ? (
        <span id={noteId} style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }}>
          {error}
        </span>
      ) : hint ? (
        <span id={noteId} className="rn-muted" style={{ fontSize: 13 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
